import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { productPath } from "@/lib/url-slugs";
import { cn } from "@/lib/utils";
import { optimizeImageUrl } from "@/lib/image-utils";
import {
  toCartProduct,
  useDbCategories,
  useDbProductImagesByProductIds,
  useDbProducts,
  useVariantStockSummary,
} from "@/hooks/use-products";
import {
  SIGNATURE_AUTOPLAY_MS,
  SIGNATURE_CAROUSEL_AUTOPLAY,
  SIGNATURE_CAROUSEL_ENABLED,
  SIGNATURE_SLIDES,
  type SignatureSlideConfig,
} from "@/lib/signature-rackets";
import type { Product } from "@/types/product";

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

interface SlideData {
  config: SignatureSlideConfig;
  product: Product;
}

const Slide = ({ config, product, priority }: SlideData & { priority: boolean }) => {
  const { lang } = useLanguage();
  const gold = config.theme === "gold";
  const href = productPath(lang, product.slug);
  const racketImage = product.images?.[0];
  const discount =
    product.compareAtPrice && product.compareAtPrice > product.price
      ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
      : 0;

  return (
    <div
      className={cn(
        "grid overflow-hidden rounded-2xl border bg-card md:grid-cols-2",
        gold
          ? "border-premium-gold/70 shadow-premium-gold"
          : "border-premium-wine/70 shadow-premium-wine"
      )}
    >
      {/* Player photo */}
      <div
        className={cn(
          "relative aspect-[4/5] w-full overflow-hidden sm:aspect-[16/10] md:aspect-auto md:min-h-[460px]",
          gold ? "bg-premium-gold-soft" : "bg-premium-wine-soft"
        )}
      >
        {config.photo.desktop ? (
          <picture>
            {config.photo.mobile && (
              <source
                media="(max-width: 767px)"
                srcSet={config.photo.mobile}
              />
            )}
            <img
              src={config.photo.desktop}
              alt={`${config.player} — ${product.name}`}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ objectPosition: config.focus.desktop }}
            />
          </picture>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
            <span className="text-sm font-medium text-muted-foreground">
              {config.player} photo
            </span>
          </div>
        )}
        <div
          className={cn(
            "pointer-events-none absolute inset-0",
            gold
              ? "bg-gradient-to-t from-premium-gold/25 via-transparent to-transparent"
              : "bg-gradient-to-t from-premium-wine/25 via-transparent to-transparent"
          )}
        />
      </div>

      {/* Content */}
      <div className="flex flex-col justify-center gap-4 p-5 md:p-8">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "rounded px-2 py-1 text-[10px] font-bold uppercase tracking-wide",
              gold
                ? "bg-premium-gold text-premium-gold-foreground"
                : "bg-premium-wine text-premium-wine-foreground"
            )}
          >
            {lang === "lv" ? "Signature rakete" : "Signature racket"}
          </span>
          <span className="text-sm font-semibold">{config.player}</span>
        </div>

        <div className="flex items-center gap-4">
          <div
            className={cn(
              "flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white md:h-32 md:w-32",
              gold ? "ring-1 ring-premium-gold/50" : "ring-1 ring-premium-wine/50"
            )}
          >
            {racketImage && (
              <img
                src={optimizeImageUrl(racketImage, { width: 300 })}
                alt={product.name}
                width={300}
                height={300}
                loading={priority ? "eager" : "lazy"}
                decoding="async"
                className="h-full w-full object-contain p-1"
              />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">{product.brand}</p>
            <h3 className="font-display text-lg font-bold leading-tight md:text-2xl">
              {product.name}
            </h3>
            <div className="mt-2 flex flex-wrap items-baseline gap-2">
              <span className="font-display text-xl font-bold text-price md:text-2xl">
                €{product.price.toFixed(2)}
              </span>
              {product.compareAtPrice && product.compareAtPrice > product.price && (
                <>
                  <span className="text-sm text-muted-foreground line-through">
                    €{product.compareAtPrice.toFixed(2)}
                  </span>
                  <span className="rounded bg-destructive px-1.5 py-0.5 text-xs font-bold text-destructive-foreground">
                    -{discount}%
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div>
          <Button asChild size="lg" className="font-display">
            <Link to={href}>{lang === "lv" ? "Apskatīt raketi" : "View racket"}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

const SignatureRackets = () => {
  const { lang } = useLanguage();
  const reducedMotion = usePrefersReducedMotion();
  const { data: dbProducts = [] } = useDbProducts({ cardFieldsOnly: true });

  const matched = useMemo(() => {
    return SIGNATURE_SLIDES.map((config) => {
      const dbProduct = dbProducts.find(
        (p) => (p.sku ?? "").trim().toUpperCase() === config.sku
      );
      return dbProduct ? { config, dbProduct } : null;
    }).filter(Boolean) as { config: SignatureSlideConfig; dbProduct: (typeof dbProducts)[number] }[];
  }, [dbProducts]);

  const productIds = useMemo(() => matched.map((m) => m.dbProduct.id), [matched]);
  const { data: images = [] } = useDbProductImagesByProductIds(productIds, {
    enabled: productIds.length > 0,
  });
  const { data: categories = [] } = useDbCategories();
  const { data: variantStockMap } = useVariantStockSummary();

  const slides: SlideData[] = useMemo(
    () =>
      matched.map(({ config, dbProduct }) => ({
        config,
        product: toCartProduct(
          dbProduct,
          images.filter((image) => image.product_id === dbProduct.id),
          categories,
          variantStockMap
        ),
      })),
    [matched, images, categories, variantStockMap]
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);
  const interactedRef = useRef(false);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("pointerDown", () => {
      interactedRef.current = true;
    });
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || !SIGNATURE_CAROUSEL_AUTOPLAY) return;
    if (reducedMotion || paused || slides.length < 2) return;
    const id = window.setInterval(() => {
      if (interactedRef.current) return;
      emblaApi.scrollNext();
    }, SIGNATURE_AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [emblaApi, reducedMotion, paused, slides.length]);

  if (!SIGNATURE_CAROUSEL_ENABLED || slides.length === 0) return null;

  return (
    <section
      className="border-y border-border bg-card/40 py-12 md:py-16"
      aria-labelledby="signature-rackets-title"
      aria-roledescription="carousel"
    >
      <div className="container mx-auto max-w-7xl px-4">
        <div className="mb-7 md:mb-9">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Starvie x PADELCORNER.LV
          </p>
          <h2 id="signature-rackets-title" className="font-display text-2xl font-bold md:text-3xl">
            {lang === "lv" ? "Starvie ambasadoru raketes" : "Starvie ambassador rackets"}
          </h2>
        </div>

        <div
          className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex touch-pan-y">
              {slides.map((slide, index) => (
                <div
                  key={slide.config.sku}
                  className="min-w-0 flex-[0_0_100%] px-0.5"
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} / ${slides.length}`}
                >
                  <Slide {...slide} priority={index === 0} />
                </div>
              ))}
            </div>
          </div>

          {slides.length > 1 && (
            <>
              <button
                type="button"
                onClick={() => emblaApi?.scrollPrev()}
                aria-label={lang === "lv" ? "Iepriekšējais" : "Previous"}
                className="absolute left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background md:flex"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={() => emblaApi?.scrollNext()}
                aria-label={lang === "lv" ? "Nākamais" : "Next"}
                className="absolute right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-background md:flex"
              >
                <ChevronRight size={20} />
              </button>

              <div className="mt-5 flex items-center justify-center gap-3">
                {slides.map((slide, index) => (
                  <button
                    key={slide.config.sku}
                    type="button"
                    onClick={() => {
                      interactedRef.current = true;
                      emblaApi?.scrollTo(index);
                    }}
                    aria-label={slide.config.player}
                    aria-current={selected === index}
                    className={cn(
                      "h-6 w-6 rounded-full p-2 transition-colors",
                      selected === index
                        ? slide.config.theme === "gold"
                          ? "bg-premium-gold"
                          : "bg-premium-wine"
                        : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                    )}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default SignatureRackets;
